import { computed } from 'mobx';
import moment from 'moment';

import { Model } from 'core/model/Model';
import { DEFAULT_DATETIME_FORMAT, timezone, formatDateTime } from 'core/util/dateUtils';
import {
  unavailableReferenceLabel,
  type AlertingStoreSlice,
  type SuppressionReference
} from 'app/stores/suppressions/suppressionReferences';
import {
  SUPPRESSION_LIFECYCLE_STATUS,
  SUPPRESSION_STATUS,
  type SuppressionLifecycleStatus,
  type SuppressionStatus
} from 'app/stores/suppressions/suppressionLifecycle';

/** Mirrors AlarmSilenceNotificationsDefinition in alarm.proto; stored exactly as it comes off the wire. */
export interface SilenceAttributes {
  id: string | number;
  key?: { value?: Record<string, string> };
  rule_id?: string;
  start_time?: string;
  end_time?: string;
  user_id?: string;
  cdate?: string;
  edate?: string;
  tableKey?: string;
}

/** Single reads and writes wrap the definition in a `silence` envelope; list responses do not. */
interface SilenceEnvelope extends Partial<SilenceAttributes> {
  silence?: Partial<SilenceAttributes>;
}

class SilenceModel extends Model<SilenceAttributes> {
  get urlRoot() {
    return '/api/ui/alertingManager/alarms/silence';
  }

  get isSilence(): true {
    return true;
  }

  /** Both models answer both questions so the mixed suppression/silence list can discriminate. */
  get isSuppression(): false {
    return false;
  }

  @computed
  get dimensionToKeyPart(): Record<string, string> {
    return this.get('key')?.value ?? {};
  }

  get ruleId(): string | undefined {
    return this.get('rule_id');
  }

  get messages() {
    return {
      create: 'Alert silence successfully created. It will take effect in a few minutes.',
      update: 'Alert silence updated successfully',
      destroy: 'Alert silence removed successfully',
      duplicate: 'Alert silence duplicated successfully'
    };
  }

  get expired(): boolean {
    const expirationTime = this.get('end_time');

    return !expirationTime ? false : timezone.momentFn(expirationTime).isBefore(moment());
  }

  // Silences keep their own timing rules, but report the same status vocabulary as suppressions
  // because both are rendered by the shared list, filters and drawer.
  get lifecycleStatus(): SuppressionLifecycleStatus {
    if (this.expired) {
      return SUPPRESSION_LIFECYCLE_STATUS.EXPIRED;
    }

    return moment(this.get('start_time')).isAfter(moment())
      ? SUPPRESSION_LIFECYCLE_STATUS.PLANNED
      : SUPPRESSION_LIFECYCLE_STATUS.ONGOING;
  }

  @computed
  get invalidReferences(): SuppressionReference[] {
    const { $alerting } = this.store as { $alerting: AlertingStoreSlice };
    const { ruleId } = this;

    if (
      !$alerting.policyCollection.hasFetched ||
      $alerting.policyCollection.requestStatus ||
      $alerting.policyCollection.error ||
      !ruleId ||
      $alerting.policyCollection.getPolicyByRuleId(ruleId)
    ) {
      return [];
    }

    return [{ type: 'policy', id: ruleId, label: unavailableReferenceLabel('policy') }];
  }

  get status(): SuppressionStatus {
    return this.invalidReferences.length > 0 ? SUPPRESSION_STATUS.INVALID : this.lifecycleStatus;
  }

  @computed
  get createdDay(): string {
    return formatDateTime(this.get('cdate'));
  }

  get expirationDetails() {
    const expirationTime = this.get('end_time');

    if (!expirationTime) {
      return { expirationDay: null, expired: false, duration: null };
    }

    const momentDate = moment(expirationTime);

    return {
      expirationDay: momentDate.format(DEFAULT_DATETIME_FORMAT),
      expired: this.expired,
      duration: moment.duration(momentDate.diff(moment())).humanize()
    };
  }

  get startTime(): string {
    return moment(this.get('start_time')).format(DEFAULT_DATETIME_FORMAT);
  }

  get startDetails() {
    const startTimeMoment = moment(this.get('start_time'));

    return {
      started: startTimeMoment.isSameOrBefore(moment()),
      futureStart: startTimeMoment.isAfter(moment()),
      diff: moment.duration(startTimeMoment.diff(moment())).humanize()
    };
  }

  @computed
  get dimensions(): string[] {
    return Object.keys(this.dimensionToKeyPart);
  }

  @computed
  get measurementDimension(): string | undefined {
    return this.dimensions.find((dimension) => dimension.endsWith('km_measurement_name'));
  }

  @computed
  get measurement(): string | undefined {
    const dimension = this.measurementDimension;

    return dimension ? this.dimensionToKeyPart[dimension] : undefined;
  }

  get userId(): string | undefined {
    return this.get('user_id');
  }

  /** Unwrap the `silence` envelope; the fields inside are already the stored shape. */
  deserialize(data: Partial<SilenceAttributes> | Record<string, Partial<SilenceAttributes>> = {}) {
    const { silence, ...rest } = data as SilenceEnvelope;

    return super.deserialize(silence ?? rest);
  }

  /** The gateway expects writes wrapped in a `silence` envelope; tolerate already-wrapped input. */
  serialize(data: Partial<SilenceAttributes> = {}) {
    const { silence, ...rest } = data as SilenceEnvelope;

    return super.serialize({ silence: silence ?? rest } as Partial<SilenceAttributes>);
  }
}

export default SilenceModel;
